import type { MouseEvent, SyntheticEvent } from 'react'
import { legacyContractDataAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchSubsDisclosure } from '@/msgdeskRuntime/subsDisclosure'
import { createPagingModel, dispatchSubsNavigation, type PagingTarget } from '@/msgdeskRuntime/subsNavigation'
import { dispatchSubsSelection } from '@/msgdeskRuntime/subsSelection'
import { actionContextForDeskMode, dispatchCheckedItemAction, itemActionsForContext, type ItemActionContext, type ItemActionId } from '@/msgdeskRuntime/itemMovements'
import { selectCheckedSubIds, selectFavsAllowed } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'

function stop(event: SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

export function ForumFoldSubsButton({ section = 0, label, count, kind = 'subs' }: {
  section?: 0 | 1
  label: string
  count?: number
  kind?: 'subs' | 'msg'
}) {
  const isMsg = kind === 'msg'
  const contract: LegacyContractName = isMsg ? 'main.foldButton' : section === 0 ? 'subs.menuToggle.answers' : 'subs.menuToggle.submsges'

  function handleFold(event: MouseEvent<HTMLElement>) {
    stop(event)
    if (isMsg) dispatchSubsDisclosure({ action: 'toggle-main' })
    else dispatchSubsDisclosure({ action: 'toggle-section', section, target: event.currentTarget })
  }

  return (
    <a href="#" {...legacyHookAttrs(contract, 'md-fold-subs md-fold-subs--forum inline-block border border-[#006699] bg-[#eef3f8] px-2 py-1 text-[10px] font-bold text-[#006699] no-underline hover:bg-[#dfe6ef]')} onClick={handleFold}>
      {label}{!isMsg && typeof count === 'number' ? ` (${count})` : ''}
    </a>
  )
}

export function ForumPagingBar({ section, currentPage = 1, totalPages = 1, registerContract = true }: {
  section: 0 | 1
  currentPage?: number
  totalPages?: number
  registerContract?: boolean
}) {
  const contract = section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
  const paging = createPagingModel(currentPage, totalPages)
  const rootClass = 'md-paging md-paging--forum inline-flex items-center border border-[#006699] bg-[#eef3f8] text-[10px]'
  const pageClass = 'md-paging__page inline-flex h-6 min-w-6 items-center justify-center border-r border-[#006699] px-1.5 font-bold text-[#006699] hover:bg-[#dfe6ef]'

  function pageClick(event: MouseEvent<HTMLElement>, page: PagingTarget) {
    stop(event)
    if (page === 'previous' && paging.previousDisabled) return
    if (page === 'next' && paging.nextDisabled) return
    dispatchSubsNavigation({ action: 'set-page', section, page, target: event.currentTarget })
  }

  return (
    <div {...(registerContract ? legacyHookAttrs(contract, rootClass) : { className: rootClass })}>
      {paging.tokens.map((token, index) => token === 'gap' ? <span key={`gap-${index}`} className="px-1">…</span> : (
        <a key={token} href="#" className={`${pageClass}${token === paging.currentPage ? ' md-paging__page--current bg-[#006699] text-white' : ''}`} data-md-legacy-class={token === paging.currentPage ? 'mcss-current_page-cssm' : 'mcss-page_el-cssm'} onClick={event => pageClick(event, token)}>{token}</a>
      ))}
      {/* Disabled edge buttons stay MOUNTED but display:none — the legacy
          prev/next click router resolves prev vs next by index inside this
          sequence span, so unmounting Prev would misroute Next as "prev". */}
      <span className="md-paging__sequence inline-flex">
        <a href="#" className={`${pageClass}${paging.previousDisabled ? ' md-hook-disabled opacity-40' : ''}`} style={paging.previousDisabled ? { display: 'none' } : undefined} data-md-legacy-class={paging.previousDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm'} onClick={event => pageClick(event, 'previous')} aria-label="Previous page">‹</a>
        <a href="#" className={`${pageClass} border-r-0${paging.nextDisabled ? ' md-hook-disabled opacity-40' : ''}`} style={paging.nextDisabled ? { display: 'none' } : undefined} data-md-legacy-class={paging.nextDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm'} onClick={event => pageClick(event, 'next')} aria-label="Next page">›</a>
      </span>
    </div>
  )
}

export function ForumRefreshSubs({ section }: { section: 0 | 1 }) {
  return <button type="button" className="md-refresh-subs md-refresh-subs--forum h-6 border border-[#006699] bg-[#eef3f8] px-2 text-[10px] font-bold text-[#006699] hover:bg-[#dfe6ef]" title="Refresh" onClick={event => dispatchSubsNavigation({ action: 'refresh', section, target: event.currentTarget })}>Refresh</button>
}

type CheckedsMode = 'msg' | 'trash' | 'drafts' | 'favs'
const actionLabels: Readonly<Record<ItemActionContext, Partial<Record<ItemActionId, string>>>> = {
  active: { 'move-to-trash': 'Delete', 'move-to-drafts': 'Draft', 'add-favorite': 'Bookmark' },
  drafts: { 'move-to-trash': 'Delete', 'restore-to-active': 'Publish' },
  trash: { 'restore-to-active': 'Restore' },
  favorites: { 'remove-favorite': 'Remove bookmark' },
}

export function ForumCheckedsInfo({ section, mode = 'msg' }: { section: 0 | 1; mode?: CheckedsMode; visual?: 'modern' | 'legacy' }) {
  const { state } = useSubsStore()
  const context = actionContextForDeskMode(mode)
  // FAVS-IDENTITY: bulk favorite actions hide with the rest of the favs UI.
  const favsAllowed = selectFavsAllowed(state)
  const sectionKey = section === 0 ? 'answers' : 'submsges'
  const checkedMode = state.renderProjection.mode === 'subs' ? sectionKey : state.renderProjection.mode
  const checkedIds = selectCheckedSubIds(state, sectionKey, checkedMode)
  const checkedSet = new Set(checkedIds)
  const currentIds = state.renderProjection.rootIds[sectionKey] || []
  const currentCount = currentIds.filter(id => checkedSet.has(id)).length
  const contract = section === 0 ? 'subs.checked.answers' : 'subs.checked.submsges'

  function runAction(event: MouseEvent<HTMLElement>, action: ItemActionId) {
    void dispatchCheckedItemAction({ action, context, section, target: event.currentTarget })
  }

  return (
    <div {...legacyContractDataAttrs(contract)} className="md-checked-info md-checked-info--forum inline-flex min-h-6 items-center gap-1 text-[10px] text-[#006699]">
      <button type="button" className="h-6 border border-[#006699] bg-[#eef3f8] px-2 font-bold hover:bg-[#dfe6ef]" title="Select visible" onClick={event => dispatchSubsSelection({ action: 'toggle-visible', section, target: event.currentTarget })}>Select {currentCount || ''}{checkedIds.length ? ` (${checkedIds.length})` : ''}</button>
      {itemActionsForContext(context).filter(action => favsAllowed || (action !== 'add-favorite' && action !== 'remove-favorite')).map(action => actionLabels[context][action] ? <button key={action} type="button" className="h-6 border border-[#006699] bg-[#eef3f8] px-2 font-bold hover:bg-[#dfe6ef]" data-md-item-action={action} onClick={event => runAction(event, action)}>{actionLabels[context][action]}</button> : null)}
    </div>
  )
}
